import type { EnvironmentType } from '../game/environment';

type BackgroundTheme = EnvironmentType | 'menu';

const A4_FREQUENCY = 440;
const NOTE_OFFSETS: Record<string, number> = {
  c: -9,
  'c#': -8,
  d: -7,
  'd#': -6,
  e: -5,
  f: -4,
  'f#': -3,
  g: -2,
  'g#': -1,
  a: 0,
  'a#': 1,
  b: 2,
};

type Pitch = string;

interface NoteEvent {
  pitch: Pitch | null;
  beats: number;
  velocity?: number;
}

interface TrackVoice {
  notes: NoteEvent[];
}

interface TrackDefinition {
  bpm: number;
  voices: TrackVoice[];
}

// Forest arena uses "Twinkle Twinkle Little Star" (public domain) for a familiar upbeat hook.
const FOREST_MELODY: NoteEvent[] = [
  { pitch: 'C4', beats: 1, velocity: 0.22 },
  { pitch: 'C4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'A4', beats: 1 },
  { pitch: 'A4', beats: 1 },
  { pitch: 'G4', beats: 2 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'D4', beats: 1 },
  { pitch: 'D4', beats: 1 },
  { pitch: 'C4', beats: 2 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'D4', beats: 2 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'D4', beats: 2 },
  { pitch: 'C4', beats: 1 },
  { pitch: 'C4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'A4', beats: 1 },
  { pitch: 'A4', beats: 1 },
  { pitch: 'G4', beats: 2 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'D4', beats: 1 },
  { pitch: 'D4', beats: 1 },
  { pitch: 'C4', beats: 2 },
];

const FOREST_BASS: NoteEvent[] = [
  { pitch: 'C3', beats: 4, velocity: 0.12 },
  { pitch: 'F2', beats: 4 },
  { pitch: 'G2', beats: 4 },
  { pitch: 'C3', beats: 4 },
  { pitch: 'C3', beats: 4 },
  { pitch: 'F2', beats: 4 },
  { pitch: 'G2', beats: 4 },
  { pitch: 'C3', beats: 4 },
];

// Desert tracks lean on "Scarborough Fair" (traditional, public domain) at a relaxed tempo.
const DESERT_MELODY: NoteEvent[] = [
  { pitch: 'D4', beats: 2, velocity: 0.2 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'A4', beats: 2 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'E4', beats: 2 },
  { pitch: 'D4', beats: 2 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'A4', beats: 2 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'F4', beats: 1 },
  { pitch: 'E4', beats: 2 },
  { pitch: 'D4', beats: 2 },
  { pitch: 'A3', beats: 2 },
];

const DESERT_BASS: NoteEvent[] = [
  { pitch: 'D3', beats: 4, velocity: 0.12 },
  { pitch: 'A2', beats: 4 },
  { pitch: 'D3', beats: 4 },
  { pitch: 'G2', beats: 4 },
  { pitch: 'D3', beats: 4 },
  { pitch: 'A2', beats: 4 },
];

// Glacier backs with a fragment of "Greensleeves" (public domain) for a chilly mood.
const GLACIER_MELODY: NoteEvent[] = [
  { pitch: 'E4', beats: 2, velocity: 0.18 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'A4', beats: 1 },
  { pitch: 'B4', beats: 2 },
  { pitch: 'A4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'E4', beats: 2 },
  { pitch: 'G4', beats: 2 },
  { pitch: 'F#4', beats: 1 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'D4', beats: 2 },
  { pitch: 'E4', beats: 2 },
  { pitch: 'B3', beats: 2 },
  { pitch: 'C4', beats: 1 },
  { pitch: 'D4', beats: 1 },
  { pitch: 'E4', beats: 2 },
  { pitch: 'D4', beats: 1 },
  { pitch: 'C4', beats: 1 },
  { pitch: 'A3', beats: 2 },
  { pitch: 'B3', beats: 2 },
  { pitch: 'E4', beats: 2 },
  { pitch: 'G4', beats: 2 },
];

const GLACIER_BASS: NoteEvent[] = [
  { pitch: 'E3', beats: 4, velocity: 0.11 },
  { pitch: 'B2', beats: 4 },
  { pitch: 'C3', beats: 4 },
  { pitch: 'G2', beats: 4 },
  { pitch: 'E3', beats: 4 },
  { pitch: 'B2', beats: 4 },
  { pitch: 'E3', beats: 4 },
  { pitch: 'B2', beats: 4 },
];

// Menu arpeggio keeps things light while waiting in the lobby.
const MENU_MELODY: NoteEvent[] = [
  { pitch: 'C4', beats: 1, velocity: 0.18 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'G4', beats: 2 },
  { pitch: 'E4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'B4', beats: 2 },
  { pitch: 'A4', beats: 1 },
  { pitch: 'G4', beats: 1 },
  { pitch: 'E4', beats: 2 },
  { pitch: 'D4', beats: 1 },
  { pitch: 'C4', beats: 3 },
];

const MENU_BASS: NoteEvent[] = [
  { pitch: 'C3', beats: 4, velocity: 0.1 },
  { pitch: 'G2', beats: 4 },
  { pitch: 'A2', beats: 4 },
  { pitch: 'F2', beats: 4 },
];

const BACKGROUND_TRACKS: Record<BackgroundTheme, TrackDefinition> = {
  forest: {
    bpm: 108,
    voices: [
      { notes: FOREST_MELODY },
      { notes: FOREST_BASS },
    ],
  },
  desert: {
    bpm: 92,
    voices: [
      { notes: DESERT_MELODY },
      { notes: DESERT_BASS },
    ],
  },
  glacier: {
    bpm: 84,
    voices: [
      { notes: GLACIER_MELODY },
      { notes: GLACIER_BASS },
    ],
  },
  menu: {
    bpm: 96,
    voices: [
      { notes: MENU_MELODY },
      { notes: MENU_BASS },
    ],
  },
};

const DEFAULT_THEME: BackgroundTheme = 'forest';

export type SuperSoundVariant = 'solar' | 'frost' | 'thunder' | 'ember' | 'void';

export class AudioManager {
  private context?: AudioContext;
  private backgroundNodes: AudioScheduledSourceNode[] = [];
  private backgroundTimer?: number;
  private unlocked = false;
  private masterGain?: GainNode;
  private muted = false;
  private currentTheme: BackgroundTheme = DEFAULT_THEME;
  private currentTrack?: TrackDefinition;
  private pianoNoiseBuffer?: AudioBuffer;

  unlock(): void {
    if (!this.context) {
      this.context = new AudioContext();
    }
    if (this.context.state === 'suspended') {
      void this.context.resume();
    }
    if (this.context) {
      this.ensureMasterGain(this.context);
    }
    this.unlocked = true;
  }

  stopAll(): void {
    this.stopBackground();
  }

  toggleMute(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.masterGain) {
      this.masterGain.gain.setValueAtTime(muted ? 0 : 0.8, this.masterGain.context.currentTime);
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  private ensureMasterGain(ctx: AudioContext): GainNode {
    if (!this.masterGain) {
      this.masterGain = ctx.createGain();
      this.masterGain.gain.value = this.muted ? 0 : 0.8;
      this.masterGain.connect(ctx.destination);
    }
    return this.masterGain;
  }
  playBackground(theme: BackgroundTheme = DEFAULT_THEME): void {
    const ctx = this.getContext();
    if (!ctx) {
      return;
    }
    if (this.currentTheme === theme && this.backgroundNodes.length > 0) {
      return;
    }
    this.stopBackground();
    this.currentTheme = theme;
    const track = BACKGROUND_TRACKS[theme] ?? BACKGROUND_TRACKS[DEFAULT_THEME];
    this.currentTrack = track;
    const startTime = ctx.currentTime + 0.12;
    this.scheduleTrack(track, startTime);
    const nextStart = startTime + this.trackDuration(track);
    this.scheduleLoop(track, nextStart);
  }

  private scheduleTrack(track: TrackDefinition, startTime: number): void {
    const ctx = this.getContext();
    if (!ctx) {
      return;
    }
    const master = this.ensureMasterGain(ctx);
    const secondsPerBeat = 60 / track.bpm;
    track.voices.forEach((voice) => this.scheduleVoice(voice, startTime, secondsPerBeat, master));
  }

  private scheduleVoice(
    voice: TrackVoice,
    startTime: number,
    secondsPerBeat: number,
    master: GainNode,
  ): void {
    let cursor = startTime;
    voice.notes.forEach((note) => {
      const duration = Math.max(0.12, note.beats * secondsPerBeat);
      if (note.pitch) {
        const frequency = this.frequencyForPitch(note.pitch);
        if (frequency > 0) {
          this.playPianoNote(master, frequency, cursor, duration, note.velocity ?? 0.18);
        }
      }
      cursor += duration;
    });
  }

  private scheduleLoop(track: TrackDefinition, nextStart: number): void {
    const ctx = this.getContext();
    if (!ctx) {
      return;
    }
    const delaySeconds = Math.max(0, nextStart - ctx.currentTime - 0.05);
    this.backgroundTimer = window.setTimeout(() => {
      const context = this.getContext();
      if (!context) {
        return;
      }
      const alignedStart = Math.max(nextStart, context.currentTime + 0.05);
      this.scheduleTrack(track, alignedStart);
      const upcoming = alignedStart + this.trackDuration(track);
      this.scheduleLoop(track, upcoming);
    }, delaySeconds * 1000);
  }

  private trackDuration(track: TrackDefinition): number {
    const secondsPerBeat = 60 / track.bpm;
    return track.voices.reduce(
      (accumulator, voice) => Math.max(accumulator, this.voiceDuration(voice, secondsPerBeat)),
      0,
    );
  }

  private voiceDuration(voice: TrackVoice, secondsPerBeat: number): number {
    return voice.notes.reduce((sum, note) => sum + note.beats * secondsPerBeat, 0);
  }

  private frequencyForPitch(pitch: Pitch): number {
    const match = /^([A-Ga-g])(#{0,1})(-?\d)$/.exec(pitch);
    if (!match) {
      return 0;
    }
    const [, letter, sharp, octaveText] = match;
    const key = `${letter}${sharp ?? ''}`.toLowerCase();
    const semitone = NOTE_OFFSETS[key];
    if (semitone === undefined) {
      return 0;
    }
    const octave = Number(octaveText);
    const octaveDelta = octave - 4;
    const steps = semitone + octaveDelta * 12;
    return A4_FREQUENCY * 2 ** (steps / 12);
  }

  private playPianoNote(master: GainNode, frequency: number, startTime: number, duration: number, velocity: number): void {
    const ctx = this.getContext();
    if (!ctx) {
      return;
    }
    // Borrow the per-note envelope from the LittleJS Piano example: fast attack, short noise hammer,
    // and a mellow triangle overtone blended with a sine fundamental.
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, startTime);
    const attack = Math.min(0.02, duration * 0.15);
    const decay = Math.min(0.12, duration * 0.3);
    const sustain = velocity * 0.55;
    gain.gain.linearRampToValueAtTime(velocity, startTime + attack);
    gain.gain.linearRampToValueAtTime(sustain, startTime + attack + decay);
    gain.gain.setValueAtTime(sustain, startTime + Math.max(duration * 0.7, attack + decay));
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
    gain.connect(master);

    const sineOsc = ctx.createOscillator();
    sineOsc.type = 'sine';
    sineOsc.frequency.setValueAtTime(frequency, startTime);
    sineOsc.connect(gain);
    sineOsc.start(startTime);
    sineOsc.stop(startTime + duration + 0.1);
    this.trackBackgroundNode(sineOsc);

    const overtoneOsc = ctx.createOscillator();
    overtoneOsc.type = 'triangle';
    overtoneOsc.frequency.setValueAtTime(frequency * 2, startTime);
    const overtoneGain = ctx.createGain();
    overtoneGain.gain.setValueAtTime(velocity * 0.35, startTime);
    overtoneGain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
    overtoneOsc.connect(overtoneGain);
    overtoneGain.connect(gain);
    overtoneOsc.start(startTime);
    overtoneOsc.stop(startTime + duration + 0.1);
    this.trackBackgroundNode(overtoneOsc);

    const noise = ctx.createBufferSource();
    noise.buffer = this.getPianoNoiseBuffer(ctx);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(velocity * 0.3, startTime);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.08);
    noise.connect(noiseGain);
    noiseGain.connect(gain);
    noise.start(startTime);
    noise.stop(startTime + 0.1);
    this.trackBackgroundNode(noise);
  }

  private getPianoNoiseBuffer(ctx: AudioContext): AudioBuffer {
    if (!this.pianoNoiseBuffer) {
      const length = Math.floor(ctx.sampleRate * 0.08);
      const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i += 1) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / length);
      }
      this.pianoNoiseBuffer = buffer;
    }
    return this.pianoNoiseBuffer;
  }

  private trackBackgroundNode(node: AudioScheduledSourceNode): void {
    this.backgroundNodes.push(node);
    node.onended = () => {
      const index = this.backgroundNodes.indexOf(node);
      if (index >= 0) {
        this.backgroundNodes.splice(index, 1);
      }
    };
  }

  stopBackground(): void {
    if (this.backgroundTimer !== undefined) {
      window.clearTimeout(this.backgroundTimer);
      this.backgroundTimer = undefined;
    }
    const stopTime = (this.context?.currentTime ?? 0) + 0.05;
    this.backgroundNodes.forEach((node) => {
      try {
        node.stop(stopTime);
      } catch (error) {
        // some nodes may already be stopped
      }
    });
    this.backgroundNodes = [];
    this.currentTrack = undefined;
  }

  playHit(strength: 'light' | 'heavy'): void {
    const ctx = this.getContext();
    if (!ctx) {
      return;
    }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    const now = ctx.currentTime;
    const frequency = strength === 'light' ? 420 : 260;
    osc.frequency.setValueAtTime(frequency, now);
    osc.frequency.exponentialRampToValueAtTime(frequency / 4, now + 0.18);
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
    osc.connect(gain);
    const master = this.ensureMasterGain(ctx);
    gain.connect(master);
    osc.start(now);
    osc.stop(now + 0.25);
  }

  playVictory(): void {
    const ctx = this.getContext();
    if (!ctx) {
      return;
    }
    const now = ctx.currentTime;
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((note, index) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = note;
      gain.gain.setValueAtTime(0.12, now + index * 0.1);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1 + index * 0.1);
      osc.connect(gain);
      const master = this.ensureMasterGain(ctx);
      gain.connect(master);
      osc.start(now + index * 0.1);
      osc.stop(now + 0.3 + index * 0.1);
    });
  }

  playSuperAttack(variant: SuperSoundVariant): void {
    const ctx = this.getContext();
    if (!ctx) {
      return;
    }
    const master = this.ensureMasterGain(ctx);
    const now = ctx.currentTime;

    const makeNoise = (duration: number, decay: number): AudioBufferSourceNode => {
      const length = Math.max(1, Math.floor(ctx.sampleRate * duration));
      const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < length; i += 1) {
        data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      return source;
    };

    const addSparkCluster = (baseTime: number, pitches: number[], gainValue: number, decay = 0.32): void => {
      pitches.forEach((pitch, index) => {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        const startTime = baseTime + index * 0.05;
        const stopTime = startTime + decay;
        osc.frequency.setValueAtTime(pitch, startTime);
        osc.frequency.exponentialRampToValueAtTime(pitch * 0.45, stopTime);
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(gainValue, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, stopTime);
        osc.connect(gain);
        gain.connect(master);
        osc.start(startTime);
        osc.stop(stopTime);
      });
    };

    switch (variant) {
      case 'solar': {
        const beam = ctx.createOscillator();
        const beamGain = ctx.createGain();
        beam.type = 'sawtooth';
        beam.frequency.setValueAtTime(360, now);
        beam.frequency.exponentialRampToValueAtTime(920, now + 0.22);
        beamGain.gain.setValueAtTime(0.26, now);
        beamGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
        beam.connect(beamGain);
        beamGain.connect(master);
        beam.start(now);
        beam.stop(now + 0.32);

        const beamOverlay = ctx.createOscillator();
        const overlayGain = ctx.createGain();
        beamOverlay.type = 'triangle';
        beamOverlay.frequency.setValueAtTime(640, now);
        beamOverlay.frequency.linearRampToValueAtTime(360, now + 0.28);
        overlayGain.gain.setValueAtTime(0.18, now);
        overlayGain.gain.exponentialRampToValueAtTime(0.001, now + 0.32);
        beamOverlay.connect(overlayGain);
        overlayGain.connect(master);
        beamOverlay.start(now);
        beamOverlay.stop(now + 0.34);

        const sub = ctx.createOscillator();
        const subGain = ctx.createGain();
        sub.type = 'sine';
        sub.frequency.setValueAtTime(140, now + 0.24);
        sub.frequency.exponentialRampToValueAtTime(46, now + 1.1);
        subGain.gain.setValueAtTime(0.34, now + 0.24);
        subGain.gain.exponentialRampToValueAtTime(0.001, now + 1.15);
        sub.connect(subGain);
        subGain.connect(master);
        sub.start(now + 0.24);
        sub.stop(now + 1.2);

        const noise = makeNoise(0.6, 1.35);
        const noiseGain = ctx.createGain();
        noiseGain.gain.setValueAtTime(0.42, now + 0.26);
        noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.74);
        noise.connect(noiseGain);
        noiseGain.connect(master);
        noise.start(now + 0.26);
        noise.stop(now + 0.74);

        addSparkCluster(now + 0.18, [880, 1180, 1560], 0.12, 0.28);
        break;
      }
      case 'frost': {
        const shard = ctx.createOscillator();
        const shardGain = ctx.createGain();
        shard.type = 'triangle';
        shard.frequency.setValueAtTime(520, now);
        shard.frequency.linearRampToValueAtTime(890, now + 0.3);
        shardGain.gain.setValueAtTime(0.22, now);
        shardGain.gain.exponentialRampToValueAtTime(0.001, now + 0.36);
        shard.connect(shardGain);
        shardGain.connect(master);
        shard.start(now);
        shard.stop(now + 0.38);

        const shimmer = ctx.createOscillator();
        const shimmerGain = ctx.createGain();
        shimmer.type = 'sine';
        shimmer.frequency.setValueAtTime(960, now + 0.26);
        shimmer.frequency.exponentialRampToValueAtTime(420, now + 0.76);
        shimmerGain.gain.setValueAtTime(0.18, now + 0.26);
        shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.84);
        const shimmerLfo = ctx.createOscillator();
        const shimmerLfoGain = ctx.createGain();
        shimmerLfo.frequency.value = 7;
        shimmerLfoGain.gain.value = 18;
        shimmerLfo.connect(shimmerLfoGain);
        shimmerLfoGain.connect(shimmer.frequency);
        shimmer.connect(shimmerGain);
        shimmerGain.connect(master);
        shimmer.start(now + 0.26);
        shimmer.stop(now + 0.88);
        shimmerLfo.start(now + 0.26);
        shimmerLfo.stop(now + 0.88);

        const frostNoise = makeNoise(0.55, 1.6);
        const frostGain = ctx.createGain();
        frostGain.gain.setValueAtTime(0.28, now + 0.28);
        frostGain.gain.exponentialRampToValueAtTime(0.001, now + 0.74);
        frostNoise.connect(frostGain);
        frostGain.connect(master);
        frostNoise.start(now + 0.28);
        frostNoise.stop(now + 0.76);

        addSparkCluster(now + 0.34, [1240, 1480, 1760], 0.09, 0.22);
        break;
      }
      case 'thunder': {
        const crack = ctx.createOscillator();
        const crackGain = ctx.createGain();
        crack.type = 'square';
        crack.frequency.setValueAtTime(240, now);
        crack.frequency.exponentialRampToValueAtTime(460, now + 0.18);
        crackGain.gain.setValueAtTime(0.32, now);
        crackGain.gain.exponentialRampToValueAtTime(0.001, now + 0.26);
        crack.connect(crackGain);
        crackGain.connect(master);
        crack.start(now);
        crack.stop(now + 0.28);

        const rumble = ctx.createOscillator();
        const rumbleGain = ctx.createGain();
        rumble.type = 'sine';
        rumble.frequency.setValueAtTime(96, now + 0.22);
        rumble.frequency.exponentialRampToValueAtTime(38, now + 1.2);
        rumbleGain.gain.setValueAtTime(0.46, now + 0.22);
        rumbleGain.gain.exponentialRampToValueAtTime(0.001, now + 1.3);
        rumble.connect(rumbleGain);
        rumbleGain.connect(master);
        rumble.start(now + 0.22);
        rumble.stop(now + 1.32);

        const strike = ctx.createOscillator();
        const strikeGain = ctx.createGain();
        strike.type = 'sawtooth';
        strike.frequency.setValueAtTime(310, now + 0.18);
        strike.frequency.linearRampToValueAtTime(160, now + 0.58);
        strikeGain.gain.setValueAtTime(0.21, now + 0.18);
        strikeGain.gain.exponentialRampToValueAtTime(0.001, now + 0.64);
        strike.connect(strikeGain);
        strikeGain.connect(master);
        strike.start(now + 0.18);
        strike.stop(now + 0.7);

        const thunderNoise = makeNoise(0.65, 1.1);
        const thunderGain = ctx.createGain();
        thunderGain.gain.setValueAtTime(0.52, now + 0.2);
        thunderGain.gain.exponentialRampToValueAtTime(0.001, now + 0.82);
        thunderNoise.connect(thunderGain);
        thunderGain.connect(master);
        thunderNoise.start(now + 0.2);
        thunderNoise.stop(now + 0.84);

        break;
      }
      case 'ember': {
        const flare = ctx.createOscillator();
        const flareGain = ctx.createGain();
        flare.type = 'sawtooth';
        flare.frequency.setValueAtTime(420, now);
        flare.frequency.linearRampToValueAtTime(720, now + 0.22);
        flareGain.gain.setValueAtTime(0.24, now);
        flareGain.gain.exponentialRampToValueAtTime(0.001, now + 0.26);
        flare.connect(flareGain);
        flareGain.connect(master);
        flare.start(now);
        flare.stop(now + 0.28);

        const emberTone = ctx.createOscillator();
        const emberGain = ctx.createGain();
        emberTone.type = 'triangle';
        emberTone.frequency.setValueAtTime(860, now + 0.18);
        emberTone.frequency.exponentialRampToValueAtTime(420, now + 0.62);
        emberGain.gain.setValueAtTime(0.18, now + 0.18);
        emberGain.gain.exponentialRampToValueAtTime(0.001, now + 0.68);
        emberTone.connect(emberGain);
        emberGain.connect(master);
        emberTone.start(now + 0.18);
        emberTone.stop(now + 0.7);

        const emberNoise = makeNoise(0.58, 1.2);
        const emberNoiseGain = ctx.createGain();
        emberNoiseGain.gain.setValueAtTime(0.44, now + 0.2);
        emberNoiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.74);
        emberNoise.connect(emberNoiseGain);
        emberNoiseGain.connect(master);
        emberNoise.start(now + 0.2);
        emberNoise.stop(now + 0.78);

        const emberCrackle = makeNoise(0.25, 0.8);
        const emberCrackleGain = ctx.createGain();
        emberCrackleGain.gain.setValueAtTime(0.28, now + 0.38);
        emberCrackleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.58);
        emberCrackle.connect(emberCrackleGain);
        emberCrackleGain.connect(master);
        emberCrackle.start(now + 0.38);
        emberCrackle.stop(now + 0.6);

        const emberSub = ctx.createOscillator();
        const emberSubGain = ctx.createGain();
        emberSub.type = 'sine';
        emberSub.frequency.setValueAtTime(120, now + 0.24);
        emberSub.frequency.exponentialRampToValueAtTime(70, now + 0.96);
        emberSubGain.gain.setValueAtTime(0.32, now + 0.24);
        emberSubGain.gain.exponentialRampToValueAtTime(0.001, now + 1.02);
        emberSub.connect(emberSubGain);
        emberSubGain.connect(master);
        emberSub.start(now + 0.24);
        emberSub.stop(now + 1.08);
        break;
      }
      case 'void': {
        const pulse = ctx.createOscillator();
        const pulseGain = ctx.createGain();
        pulse.type = 'sine';
        pulse.frequency.setValueAtTime(320, now);
        pulse.frequency.linearRampToValueAtTime(220, now + 0.32);
        pulseGain.gain.setValueAtTime(0.24, now);
        pulseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
        pulse.connect(pulseGain);
        pulseGain.connect(master);
        pulse.start(now);
        pulse.stop(now + 0.42);

        const detune = ctx.createOscillator();
        const detuneGain = ctx.createGain();
        detune.type = 'sawtooth';
        detune.frequency.setValueAtTime(180, now + 0.2);
        detune.frequency.linearRampToValueAtTime(110, now + 0.64);
        detuneGain.gain.setValueAtTime(0.2, now + 0.2);
        detuneGain.gain.exponentialRampToValueAtTime(0.001, now + 0.68);
        detune.connect(detuneGain);
        detuneGain.connect(master);
        detune.start(now + 0.2);
        detune.stop(now + 0.7);

        const lfo = ctx.createOscillator();
        const lfoGain = ctx.createGain();
        lfo.frequency.value = 3;
        lfoGain.gain.value = 18;
        lfo.connect(lfoGain);
        lfoGain.connect(detune.frequency);
        lfoGain.connect(pulse.frequency);
        lfo.start(now + 0.2);
        lfo.stop(now + 0.7);

        const voidNoise = makeNoise(0.7, 1.1);
        const voidNoiseGain = ctx.createGain();
        voidNoiseGain.gain.setValueAtTime(0.3, now + 0.24);
        voidNoiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.86);
        voidNoise.connect(voidNoiseGain);
        voidNoiseGain.connect(master);
        voidNoise.start(now + 0.24);
        voidNoise.stop(now + 0.88);

        const voidSub = ctx.createOscillator();
        const voidSubGain = ctx.createGain();
        voidSub.type = 'triangle';
        voidSub.frequency.setValueAtTime(150, now + 0.24);
        voidSub.frequency.exponentialRampToValueAtTime(60, now + 1.2);
        voidSubGain.gain.setValueAtTime(0.28, now + 0.24);
        voidSubGain.gain.exponentialRampToValueAtTime(0.001, now + 1.26);
        voidSub.connect(voidSubGain);
        voidSubGain.connect(master);
        voidSub.start(now + 0.24);
        voidSub.stop(now + 1.24);
        break;
      }
      default:
        break;
    }
  }

  playDefeat(): void {
    const ctx = this.getContext();
    if (!ctx) {
      return;
    }
    const master = this.ensureMasterGain(ctx);
    const now = ctx.currentTime;

    const padOsc = ctx.createOscillator();
    const padGain = ctx.createGain();
    padOsc.type = 'sine';
    padOsc.frequency.setValueAtTime(110, now);
    padGain.gain.setValueAtTime(0.1, now);
    padGain.gain.linearRampToValueAtTime(0.002, now + 2);
    padOsc.connect(padGain);
    padGain.connect(master);
    padOsc.start(now);
    padOsc.stop(now + 2.2);

    const melody = [392, 349.23, 293.66, 261.63];
    melody.forEach((note, index) => {
      const start = now + index * 0.35;
      const end = start + 0.6;
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(note, start);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.25, start);
      gain.gain.exponentialRampToValueAtTime(0.002, end);
      const vibrato = ctx.createOscillator();
      const vibratoGain = ctx.createGain();
      vibrato.frequency.value = 5;
      vibratoGain.gain.value = 6;
      vibrato.connect(vibratoGain);
      vibratoGain.connect(osc.frequency);
      vibrato.start(start);
      vibrato.stop(end);
      osc.connect(gain);
      gain.connect(master);
      osc.start(start);
      osc.stop(end);
    });

    const chimeOsc = ctx.createOscillator();
    const chimeGain = ctx.createGain();
    chimeOsc.type = 'square';
    chimeOsc.frequency.setValueAtTime(523.25, now + 0.2);
    chimeOsc.frequency.exponentialRampToValueAtTime(196, now + 1.2);
    chimeGain.gain.setValueAtTime(0.08, now + 0.2);
    chimeGain.gain.exponentialRampToValueAtTime(0.001, now + 1.3);
    chimeOsc.connect(chimeGain);
    chimeGain.connect(master);
    chimeOsc.start(now + 0.2);
    chimeOsc.stop(now + 1.4);
  }

  private getContext(): AudioContext | undefined {
    if (!this.unlocked) {
      return undefined;
    }
    if (!this.context) {
      this.context = new AudioContext();
    }
    return this.context;
  }
}
